// src/components/Admin/QuestionsAll.jsx
import React, { useState, useEffect } from "react";
import {
  Database,
  Filter,
  Search,
  Calendar,
  TrendingUp,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Eye,
  EyeOff,
  BarChart3
} from "lucide-react";

import API_BASE_URL from "../../services/config";

import { authFetch } from "../../auth";

// Static config outside component
const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "answered", label: "Answered" },
  { value: "archived", label: "Archived" }
];

const CATEGORY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "Daily Life & Dharma", label: "Daily Life & Dharma" },
  { value: "Relationships & Family", label: "Relationships & Family" },
  { value: "Career & Duty", label: "Career & Duty" },
  { value: "Festivals & Rituals", label: "Festivals & Rituals" },
  { value: "Philosophy & Spiritual Growth", label: "Philosophy & Spiritual Growth" }
];

// Small helpers
const safeString = (v) => (typeof v === "string" ? v : "");
const formatDate = (value) => {
  if (!value) return "Unknown";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "Unknown";
  return d.toLocaleDateString();
};

const QuestionsAll = () => {
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [viewMode, setViewMode] = useState("list"); // "list" or "grid"

  // Load from backend (with JWT using authFetch)
  useEffect(() => {
    fetchAllQuestions();
  }, []);

  const fetchAllQuestions = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await authFetch(`${API_BASE_URL}/api/admin/questions`, {
        method: "GET"
      });

      if (!res.ok) {
        throw new Error("Failed to load questions (" + res.status + ")");
      }

      const data = await res.json();
      const arrayData = Array.isArray(data) ? data : [];
      setQuestions(arrayData);
      setFilteredQuestions(arrayData);
    } catch (err) {
      console.error("Error fetching all questions:", err);
      setError(err.message || "Failed to load questions.");
      setQuestions([]);
      setFilteredQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Re-filter when inputs change
  useEffect(() => {
    let filtered = questions.slice();

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((q) => {
        const title = safeString(q.title).toLowerCase();
        const body = safeString(q.body).toLowerCase();
        const answer = safeString(q.answer).toLowerCase();
        const userName = safeString(q.user && q.user.name).toLowerCase();

        return (
          title.includes(term) ||
          body.includes(term) ||
          answer.includes(term) ||
          userName.includes(term)
        );
      });
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((q) => {
        if (!q.status) return false;
        return q.status.toLowerCase() === statusFilter.toLowerCase();
      });
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter((q) => q.category === categoryFilter);
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const cutoff = new Date();

      if (dateFilter === "today") {
        cutoff.setDate(now.getDate() - 1);
      } else if (dateFilter === "week") {
        cutoff.setDate(now.getDate() - 7);
      } else if (dateFilter === "month") {
        cutoff.setMonth(now.getMonth() - 1);
      }

      filtered = filtered.filter((q) => {
        if (!q.createdAt) return false;
        const d = new Date(q.createdAt);
        return !isNaN(d.getTime()) && d > cutoff;
      });
    }

    setFilteredQuestions(filtered);
  }, [questions, searchTerm, statusFilter, categoryFilter, dateFilter]);

  const getStatusBadge = (statusRaw) => {
    const status = statusRaw ? statusRaw.toLowerCase() : "unknown";

    let colorClass = "bg-gray-100 text-gray-800";
    let icon = <AlertCircle className="h-3 w-3" />;

    if (status === "pending") {
      colorClass = "bg-yellow-100 text-yellow-800";
      icon = <Clock className="h-3 w-3" />;
    } else if (status === "answered") {
      colorClass = "bg-green-100 text-green-800";
      icon = <CheckCircle className="h-3 w-3" />;
    } else if (status === "archived") {
      colorClass = "bg-blue-100 text-blue-800";
      icon = <EyeOff className="h-3 w-3" />;
    }

    const label =
      statusRaw && typeof statusRaw === "string"
        ? statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1).toLowerCase()
        : "Unknown";

    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium " +
          colorClass
        }
      >
        {icon}
        <span className="ml-1">{label}</span>
      </span>
    );
  };

  const getCategoryBorder = (category) => {
    if (category === "Daily Life & Dharma") return "border-l-blue-500";
    if (category === "Relationships & Family") return "border-l-pink-500";
    if (category === "Career & Duty") return "border-l-indigo-500";
    if (category === "Festivals & Rituals") return "border-l-amber-500";
    if (category === "Philosophy & Spiritual Growth") return "border-l-emerald-500";
    return "border-l-gray-500";
  };

  const toggleQuestionSelection = (id) => {
    if (!id) return;

    setSelectedQuestions((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      } else {
        return prev.concat(id);
      }
    });
  };

  const selectAllQuestions = () => {
    const allIds = filteredQuestions
      .map((q) => q.id)
      .filter((id) => typeof id === "string" && id.length > 0);

    if (selectedQuestions.length === allIds.length && allIds.length > 0) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(allIds);
    }
  };

  const exportSelected = () => {
    const selected = questions.filter((q) => selectedQuestions.includes(q.id));
    if (selected.length === 0) {
      return;
    }

    const header = [
      "ID",
      "Title",
      "Status",
      "Category",
      "User",
      "Created At",
      "Has Answer"
    ];

    const rows = selected.map((q) => {
      const id = safeString(q.id).replace(/"/g, '""');
      const title = safeString(q.title).replace(/"/g, '""');
      const status = safeString(q.status).replace(/"/g, '""');
      const category = safeString(q.category).replace(/"/g, '""');
      const userName = safeString(q.user && q.user.name).replace(/"/g, '""');
      const created = formatDate(q.createdAt);
      const hasAnswer = q.answer ? "Yes" : "No";

      return [
        '"' + id + '"',
        '"' + title + '"',
        '"' + status + '"',
        '"' + category + '"',
        '"' + userName + '"',
        created,
        hasAnswer
      ];
    });

    const csvContent = [header].concat(rows).map((r) => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "questions-selected-" + new Date().toISOString().split("T")[0] + ".csv";
    a.click();

    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-500 border-t-transparent"></div>
        <p className="text-indigo-600 font-medium">Loading all questions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <Database className="h-8 w-8" />
              <h1 className="text-2xl font-bold">All Questions</h1>
            </div>
            <p className="text-indigo-100">
              Comprehensive view of all questions in the system
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{questions.length}</div>
            <p className="text-indigo-100 text-sm">Total Questions</p>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Answered */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Answered</p>
              <p className="text-xl font-bold">
                {
                  questions.filter((q) => {
                    if (!q.status) return false;
                    return q.status.toLowerCase() === "answered";
                  }).length
                }
              </p>
            </div>
          </div>
        </div>

        {/* Pending */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-xl font-bold">
                {
                  questions.filter((q) => {
                    if (!q.status) return false;
                    return q.status.toLowerCase() === "pending";
                  }).length
                }
              </p>
            </div>
          </div>
        </div>

        {/* Unique Users */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Unique Users</p>
              <p className="text-xl font-bold">
                {
                  new Set(
                    questions.map((q) => q.user && q.user.id).filter(Boolean)
                  ).size
                }
              </p>
            </div>
          </div>
        </div>

        {/* This Week */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">This Week</p>
              <p className="text-xl font-bold">
                {
                  questions.filter((q) => {
                    if (!q.createdAt) return false;
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    const d = new Date(q.createdAt);
                    return !isNaN(d.getTime()) && d > weekAgo;
                  }).length
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters + Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-400" />

              {/* Status */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* Category */}
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>

              {/* Date */}
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* View toggle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode("list")}
                className={
                  "p-2 rounded-lg " +
                  (viewMode === "list"
                    ? "bg-indigo-100 text-indigo-600"
                    : "text-gray-500")
                }
              >
                <BarChart3 className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={
                  "p-2 rounded-lg " +
                  (viewMode === "grid"
                    ? "bg-indigo-100 text-indigo-600"
                    : "text-gray-500")
                }
              >
                <Database className="h-5 w-5" />
              </button>
            </div>

            {/* Select all */}
            <button
              onClick={selectAllQuestions}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              {selectedQuestions.length === filteredQuestions.length &&
              filteredQuestions.length > 0
                ? "Deselect All"
                : "Select All"}
            </button>

            {/* Export selected */}
            {selectedQuestions.length > 0 && (
              <button
                onClick={exportSelected}
                className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg hover:opacity-90"
              >
                <Download className="h-4 w-4" />
                <span>Export ({selectedQuestions.length})</span>
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
            {error}
          </div>
        )}
      </div>

      {/* Questions List/Grid */}
      <div>
        {filteredQuestions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Database className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No questions found
            </h3>
            <p className="text-gray-600">Try adjusting your filters or check back later</p>
          </div>
        ) : viewMode === "list" ? (
          <div className="space-y-4">
            {filteredQuestions.map((q) => (
              <div
                key={q.id}
                className={
                  "bg-white rounded-xl border border-l-4 " +
                  getCategoryBorder(q.category) +
                  " shadow-sm hover:shadow-md transition-all"
                }
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          {getStatusBadge(q.status)}
                          <span className="text-sm text-gray-500">
                            {formatDate(q.createdAt)}
                          </span>
                        </div>

                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={selectedQuestions.includes(q.id)}
                            onChange={() => toggleQuestionSelection(q.id)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <button className="text-indigo-600 hover:text-indigo-800">
                            <Eye className="h-5 w-5" />
                          </button>
                        </div>
                      </div>

                      <h3 className="font-bold text-gray-900 mb-2">
                        {q.title || "Untitled question"}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {q.body}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {(q.user && q.user.name) || "Anonymous"}
                          </span>
                          <span className="text-sm">
                            Category:{" "}
                            <span className="font-medium">
                              {q.category || "Uncategorized"}
                            </span>
                          </span>
                          {q.answer && (
                            <span className="text-green-600 font-medium">
                              Answered
                            </span>
                          )}
                        </div>

                        <div className="text-sm text-gray-500">
                          ID:{" "}
                          {typeof q.id === "string"
                            ? q.id.slice(0, 8) + "..."
                            : "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Grid view
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredQuestions.map((q) => (
              <div
                key={q.id}
                className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all hover:border-indigo-300"
              >
                <div className="flex justify-between items-start mb-3">
                  {getStatusBadge(q.status)}
                  <input
                    type="checkbox"
                    checked={selectedQuestions.includes(q.id)}
                    onChange={() => toggleQuestionSelection(q.id)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </div>

                <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">
                  {q.title || "Untitled question"}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {q.body}
                </p>

                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center">
                    <Users className="h-3 w-3 mr-1" />
                    {(q.user && q.user.name) || "Anonymous"}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {formatDate(q.createdAt)}
                  </div>
                  <div className="font-medium text-gray-700">
                    Category: {q.category || "Uncategorized"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {filteredQuestions.length} of {questions.length} questions
            {selectedQuestions.length > 0 &&
              " • " + selectedQuestions.length + " selected"}
          </div>

          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              Last updated: {new Date().toLocaleTimeString()}
            </div>
            <button
              onClick={fetchAllQuestions}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionsAll;
